import type { MutableState } from '../State';
import { sendAnalyticsEvents } from '../analytics';
import type {
  AnalyticsAPI,
  SyncActionAPI,
} from '../config/actions/createActions';
import { grantMatch2Rewards } from '../match2/match2.modifiers';
import { consolidateRewards } from '../match2/rewards/match2-rewards.getters';
import type { Match2Reward } from '../match2/rewards/match2-rewards.ruleset';
import {
  getDreamDriftCompleteAnalyticsEvent,
  getDreamDriftCurrencyGrantProperties,
} from './dreamDrift.analytics';
import {
  dreamDriftCanSelectHero,
  dreamDriftCanStartLevel,
  dreamDriftCanStartInstance,
  dreamDriftGetCheckpoint,
  dreamDriftGetFinalNode,
  dreamDriftGetMilestones,
  dreamDriftGetInstanceRemainingTime,
  dreamDriftGetMapProgress,
  dreamDriftHasRewardsAvailable,
  dreamDriftHasStartedCurrentEvent,
  dreamDriftIsAvailable,
} from './dreamDrift.getters';
import { type DreamDriftHero, dreamDriftHeroes } from './dreamDrift.ruleset';

/**
 * Get the mutable Dream Drift root, creating it for first-time players.
 *
 * @param state Mutable player state.
 * @returns Initialized Dream Drift root state.
 */
function dreamDriftEnsureState(state: MutableState) {
  // Create the optional root only when Dream Drift first writes state.
  if (!state.dreamDrift) state.dreamDrift = {};

  // Return the initialized root for the requested mutation.
  return state.dreamDrift;
}

/**
 * Record when the player sees an available Dream Drift event.
 *
 * @param state Mutable player state.
 * @param now Server-authoritative current timestamp.
 * @returns Whether the seen timestamp was written.
 */
export function dreamDriftSetSeenAt(state: MutableState, now: number): boolean {
  // Seen state should only be recorded while Dream Drift is available.
  if (!dreamDriftIsAvailable(state, now)) return false;

  // Retrieve the lazily initialized Dream Drift state.
  const dreamDrift = dreamDriftEnsureState(state);

  // Refresh the server-authoritative timestamp on every feature view.
  dreamDrift.seenAt = now;
  return true;
}

/**
 * Start a playable Dream Drift instance with the player's initial hero choice.
 *
 * @param state Mutable player state.
 * @param args Server timestamp and selected hero for the new instance.
 * @returns New instance start timestamp, or undefined when a gate fails.
 */
export function dreamDriftStartInstance(
  state: MutableState,
  args: { now: number; hero: DreamDriftHero },
): number | undefined {
  // Event, pet, completion, and active-instance gates must all pass.
  if (!dreamDriftCanStartInstance(state, args.now)) return undefined;

  // Guard against invalid runtime payloads bricking the instance.
  if (!dreamDriftHeroes.includes(args.hero)) return undefined;

  // Retain the previous count only when its instance belongs to this event.
  const hasInstanceInCurrentEvent = dreamDriftHasStartedCurrentEvent(
    state,
    args.now,
  );

  // No stored instance means the next instance is the first in its window.
  const instanceNumber = state.dreamDrift?.instance?.instanceNumber ?? 0;
  const currentNumber = hasInstanceInCurrentEvent ? instanceNumber : 0;

  // Replace any previous terminal instance with a fresh playable run.
  const dreamDrift = dreamDriftEnsureState(state);
  dreamDrift.instance = {
    startedAt: args.now,
    // Increment the retained count, or zero for a new event, to one-based form.
    instanceNumber: currentNumber + 1,
    progress: 0,
    playing: 0,
    hero: args.hero,
  };

  // Return the server timestamp used as the instance identifier.
  return args.now;
}

/**
 * Claim rewards available from the retained Dream Drift instance.
 *
 * @param state Mutable player state containing the retained instance.
 * @param now Server-authoritative current timestamp.
 * @param api Action API used to grant rewards and emit analytics.
 * @returns Consolidated rewards granted by this claim, or an empty list.
 */
export function dreamDriftClaimRewards(
  state: MutableState,
  now: number,
  api: SyncActionAPI,
): Match2Reward[] {
  // Only the shared availability rules may authorize a reward claim.
  if (!dreamDriftHasRewardsAvailable(state, now)) return [];

  // Availability guarantees a retained instance for reward selection.
  const instance = state.dreamDrift?.instance;
  if (!instance) return [];

  // Completed runs receive every milestone; expired runs qualify upon reaching one.
  const maxLevelReached = Math.max(
    instance.maxLevelReached ?? 0,
    instance.progress,
  );
  const milestones = dreamDriftGetMilestones();
  const availableMilestones =
    instance.result === 'completed' ? milestones : milestones.slice(0, -1);
  const requiredProgressOffset = instance.result === 'completed' ? 0 : 1;
  const reachedMilestones = availableMilestones.filter(
    ({ level }) => level <= maxLevelReached + requiredProgressOffset,
  );
  const milestonesToClaim =
    instance.result === 'completed' ? milestones : reachedMilestones.slice(-1);

  // Close the claim before granting so repeated requests remain idempotent.
  instance.claimed = true;

  // Preserve the existing Dream Drift identity on every currency grant event.
  const currencyGrantProperties = getDreamDriftCurrencyGrantProperties(state);

  // Grant each selected milestone with its existing analytics attribution.
  for (const milestone of milestonesToClaim) {
    const rewardSubFeature =
      milestone === milestones.at(-1)
        ? 'completion_chest'
        : `milestone_${milestone.level}`;

    grantMatch2Rewards(
      state,
      milestone.rewards,
      {
        feature: 'dream_drift',
        $subFeature: rewardSubFeature,
      },
      api,
      currencyGrantProperties
        ? {
            getAdditionalProperties: () => currencyGrantProperties,
          }
        : undefined,
    );
  }

  // Consolidate duplicate reward types for the single completion chest presentation.
  const rewards = milestonesToClaim.flatMap(({ rewards }) => rewards);
  return consolidateRewards(rewards);
}

/**
 * Resolve an unfinished Dream Drift instance.
 *
 * @param state Mutable player state.
 * @param now Server-authoritative current timestamp.
 * @param api Analytics API used when the instance expires.
 * @returns Nothing.
 */
export function dreamDriftResolveInstance(
  state: MutableState,
  now: number,
  api: AnalyticsAPI,
): void {
  // Missing instances have no progress to reconcile.
  const instance = state.dreamDrift?.instance;
  if (!instance) return;

  // Terminal instances need no further progression reconciliation.
  if (instance.result) return;

  // Timed-out instances expire regardless of whether a level was interrupted.
  const instanceHasExpired =
    dreamDriftGetInstanceRemainingTime(state, now) === 0;
  if (instanceHasExpired) {
    // Retain a one-time expiry receipt even when the pin is already at the start.
    if (instance.pendingProgress === undefined) {
      instance.pendingProgress = instance.progress;
    }

    // Resolve the existing terminal outcome without changing its progression.
    instance.result = 'expired';
    instance.playing = 0;

    // Emit the terminal event from the same transition that resolves the run.
    const completionEvent = getDreamDriftCompleteAnalyticsEvent(state);
    if (completionEvent) sendAnalyticsEvents(api, [completionEvent]);

    // The expired instance remains available for an explicit reward claim.
    return;
  }

  // An idle instance remains resumable after login.
  if (instance.playing === 0) return;

  // A level interrupted by login rolls back before its checkpoint level.
  const checkpoint = dreamDriftGetCheckpoint(state, instance.playing);
  const checkpointProgress = checkpoint === 0 ? 0 : checkpoint - 1;

  // Retain the last visible position only when rollback moves the pin.
  if (checkpointProgress !== instance.progress) {
    if (instance.pendingProgress === undefined) {
      instance.pendingProgress = instance.progress;
    }

    // Preserve the highest cleared node before login rollback lowers progress.
    instance.maxLevelReached = Math.max(
      instance.maxLevelReached ?? 0,
      instance.progress,
    );
  }

  // Apply the existing rollback after its visual source is retained.
  instance.progress = checkpointProgress;
  instance.playing = 0;

  // Interrupted progress remains unclaimed until completion or event end.
}

/**
 * Acknowledge a Dream Drift map transition after it has been presented.
 *
 * @param state Mutable player state.
 * @param args Instance, source, destination, and server timestamp to validate.
 * @returns Whether the matching visual receipt was acknowledged.
 */
export function dreamDriftAcknowledgeMapTransition(
  state: MutableState,
  args: {
    startedAt: number;
    pendingProgress: number;
    progress: number;
    now: number;
  },
): boolean {
  // The acknowledgement must target the current stored instance.
  const instance = state.dreamDrift?.instance;
  if (!instance || instance.startedAt !== args.startedAt) return false;

  // The pending source must match the transition that was presented.
  if (instance.pendingProgress !== args.pendingProgress) return false;

  // Compare the presented destination with the latest authoritative position.
  const currentProgress = dreamDriftGetMapProgress(state, args.now);
  if (currentProgress !== args.progress) {
    // Preserve the position just displayed as the source of the newer change.
    instance.pendingProgress = args.progress;
    return true;
  }

  // The matching visual transition has been consumed successfully.
  delete instance.pendingProgress;
  return true;
}

/**
 * Select the hero for the next Dream Drift level.
 *
 * @param state Mutable player state.
 * @param args Instance identifier, selected hero, and server timestamp.
 * @returns Whether the requested hero selection is accepted.
 */
export function dreamDriftSwitchHero(
  state: MutableState,
  args: { startedAt: number; hero: DreamDriftHero; now: number },
): boolean {
  // The request must target the current stored instance.
  const instance = state.dreamDrift?.instance;
  if (!instance || instance.startedAt !== args.startedAt) return false;

  // The instance must still be ongoing at server-authoritative time.
  if (instance.result) return false;

  // Only heroes in the current ruleset can become active.
  if (!dreamDriftHeroes.includes(args.hero)) return false;

  // Hero changes are allowed whenever an idle instance can prepare its next level.
  if (!dreamDriftCanSelectHero(state, args.now)) return false;

  // Store the selected hero for the next level attempt.
  instance.hero = args.hero;
  return true;
}

/**
 * Start the next expected Dream Drift level attempt.
 *
 * @param state Mutable player state.
 * @param args Instance identifier, requested node, and server timestamp.
 * @returns Whether a new level attempt was started.
 */
export function dreamDriftStartLevel(
  state: MutableState,
  args: { startedAt: number; node: number; now: number },
): boolean {
  // The request must target the current stored instance.
  const instance = state.dreamDrift?.instance;
  if (!instance || instance.startedAt !== args.startedAt) return false;

  // The current game state must allow a new Dream Drift level attempt.
  if (!dreamDriftCanStartLevel(state, args.now)) return false;

  // Only the node after the highest cleared progress can start.
  const nextNode = instance.progress + 1;
  if (args.node !== nextNode) return false;

  // Mark the validated node as the active level attempt.
  instance.playing = args.node;

  // Start every Dream Drift attempt with no purchased Match-2 continues.
  state.match2.continues = 0;

  // The new level attempt was started successfully.
  return true;
}

/** Result of an accepted Dream Drift level finish. */
export type DreamDriftLevelFinishResult = {
  /** Result reported for the finished Dream Drift level attempt. */
  result: 'win' | 'lose' | 'quit';
};

/**
 * Finish the active Dream Drift level attempt and update progression.
 *
 * @param state Mutable player state.
 * @param args Instance identifier and result.
 * @returns Accepted finish result, or undefined for stale or invalid input.
 */
export function dreamDriftFinishLevel(
  state: MutableState,
  args: {
    startedAt: number;
    result: DreamDriftLevelFinishResult['result'];
  },
): DreamDriftLevelFinishResult | undefined {
  // The request must target the current ongoing instance.
  const instance = state.dreamDrift?.instance;
  if (!instance || instance.startedAt !== args.startedAt) return undefined;
  if (instance.result) return undefined;

  // A level must be actively playing before its result can be accepted.
  if (instance.playing === 0) return undefined;

  // Only supported gameplay results may change progression.
  if (
    args.result !== 'win' &&
    args.result !== 'lose' &&
    args.result !== 'quit'
  ) {
    return undefined;
  }

  // Preserve the active node before clearing the playing state.
  const node = instance.playing;

  // Clear continues from the finished Dream Drift attempt.
  state.match2.continues = 0;

  // A loss or quit clears the attempt and restores the applicable checkpoint.
  if (args.result !== 'win') {
    // Resolve the progress immediately before the checkpoint replay level.
    const checkpoint = dreamDriftGetCheckpoint(state, node);
    const checkpointProgress = checkpoint === 0 ? 0 : checkpoint - 1;

    // Retain the earliest unseen source when rollback moves the pin.
    if (checkpointProgress !== instance.progress) {
      if (instance.pendingProgress === undefined) {
        instance.pendingProgress = instance.progress;
      }

      // Preserve the highest cleared node before the loss lowers progress.
      instance.maxLevelReached = Math.max(
        instance.maxLevelReached ?? 0,
        instance.progress,
      );
    }

    // Apply the existing checkpoint rollback after retaining its visual source.
    instance.progress = checkpointProgress;
    instance.playing = 0;

    return {
      result: args.result,
    };
  }

  // Retain the earliest unseen source before a win advances progression.
  if (instance.pendingProgress === undefined) {
    instance.pendingProgress = instance.progress;
  }

  // A win records the playing node as the highest cleared progress.
  instance.progress = node;
  instance.maxLevelReached = Math.max(instance.maxLevelReached ?? 0, node);
  instance.playing = 0;

  // The final milestone completes the instance in the same level finish.
  if (node === dreamDriftGetFinalNode()) {
    instance.result = 'completed';
  }

  // Return the accepted result while map opening owns reward settlement.
  return {
    result: args.result,
  };
}
